<template>
    <div v-if="rows && rows.length > 0" class="col-md-12">
        <div class="table-responsive presentation-price-selector">
            <h5 class="separator-title">
                Lista de Precios

                <el-tooltip class="item" effect="dark" placement="top">
                    <div slot="content">
                        <template v-if="allowSelection">
                            Seleccione el precio de presentación
                            que desea utilizar.
                        </template>

                        <template v-else>
                            Se utilizará el precio predeterminado.
                        </template>
                    </div>

                    <i class="fa fa-info-circle"></i>
                </el-tooltip>
            </h5>

            <table class="table">
                <thead class="bg-light">
                    <tr>
                        <th class="text-center">
                            Unidad
                        </th>

                        <th class="text-center">
                            Descripción
                        </th>

                        <th class="text-center">
                            Factor
                        </th>

                        <th v-if="showMultiplePrices" class="text-center">
                            Precio 1
                        </th>

                        <th class="text-center">
                            {{
                                showMultiplePrices
                                    ? 'Precio 2'
                                    : 'Precio'
                            }}
                        </th>

                        <th v-if="showMultiplePrices" class="text-center">
                            Precio 3
                        </th>

                        <th v-if="showMultiplePrices" class="text-center">
                            Precio predeterminado
                        </th>

                        <th></th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="row in rows" :key="row.id">
                        <td class="text-center">
                            {{ row.unit_type_id }}
                        </td>

                        <td class="text-center">
                            {{ row.description }}
                        </td>

                        <td class="text-center">
                            {{ row.quantity_unit }}
                        </td>

                        <!-- Precio 1 -->
                        <td v-if="showMultiplePrices" class="text-center">
                            <button v-if="allowSelection" type="button" class="presentation-price-option" :class="{
                                selected:
                                    isSelectedPrice(
                                        row,
                                        1
                                    )
                            }" :disabled="!isAvailable(row, 1)
                                    " @click.prevent="
                                    selectPrice(row, 1)
                                    ">
                                {{ formatPrice(row.price1) }}
                            </button>

                            <span v-else>
                                {{ formatPrice(row.price1) }}
                            </span>
                        </td>

                        <!-- Precio 2 -->
                        <td class="text-center">
                            <button v-if="allowSelection" type="button" class="presentation-price-option" :class="{
                                selected:
                                    isSelectedPrice(
                                        row,
                                        2
                                    )
                            }" :disabled="!isAvailable(row, 2)
                                    " @click.prevent="
                                    selectPrice(row, 2)
                                    ">
                                {{ formatPrice(row.price2) }}
                            </button>

                            <span v-else>
                                {{ formatPrice(row.price2) }}
                            </span>
                        </td>

                        <!-- Precio 3 -->
                        <td v-if="showMultiplePrices" class="text-center">
                            <button v-if="allowSelection" type="button" class="presentation-price-option" :class="{
                                selected:
                                    isSelectedPrice(
                                        row,
                                        3
                                    )
                            }" :disabled="!isAvailable(row, 3)
                                    " @click.prevent="
                                    selectPrice(row, 3)
                                    ">
                                {{ formatPrice(row.price3) }}
                            </button>

                            <span v-else>
                                {{ formatPrice(row.price3) }}
                            </span>
                        </td>

                        <td v-if="showMultiplePrices" class="text-center">
                            Precio {{ row.price_default }}
                        </td>

                        <td class="text-right">
                            <button type="button" class="
                                    btn
                                    waves-effect
                                    waves-light
                                    btn-xs
                                " :class="isSelectedPresentation(row)
                                        ? 'btn-success'
                                        : 'btn-secondary'
                                    " title="Seleccionar presentación" @click.prevent="
                                    togglePresentation(row)
                                    ">
                                <i class="el-icon-check"></i>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
    name: 'PresentationPriceSelector',

    props: {
        rows: {
            type: Array,
            default: () => [],
        },

        selectedPresentation: {
            type: Object,
            default: () => ({}),
        },
    },

    computed: {
        ...mapState([
            'config',
        ]),

        showMultiplePrices() {
            if (
                this.config &&
                typeof this.config
                    .show_multiple_presentation_prices !==
                'undefined'
            ) {
                return Boolean(
                    this.config
                        .show_multiple_presentation_prices
                )
            }

            return true
        },

        allowSelection() {
            if (!this.showMultiplePrices) {
                return false
            }

            if (
                this.config &&
                typeof this.config
                    .allow_select_presentation_prices !==
                'undefined'
            ) {
                return Boolean(
                    this.config
                        .allow_select_presentation_prices
                )
            }

            return false
        },
    },

    methods: {
        getDefaultPriceIndex(row) {
            if (!this.showMultiplePrices) {
                return 2
            }

            const priceDefault =
                Number(row.price_default)

            return [1, 2, 3].includes(priceDefault)
                ? priceDefault
                : 2
        },

        getPrice(row, priceIndex) {
            return Number(
                row[`price${priceIndex}`] || 0
            )
        },

        formatPrice(value) {
            return Number(value || 0).toFixed(4)
        },

        isAvailable(row, priceIndex) {
            return this.getPrice(
                row,
                priceIndex
            ) > 0
        },

        isSelectedPresentation(row) {
            return (
                this.selectedPresentation &&
                Number(this.selectedPresentation.id) ===
                Number(row.id)
            )
        },

        isSelectedPrice(row, priceIndex) {
            if (!this.isSelectedPresentation(row)) {
                return false
            }

            const selectedPrice = Number(
                this.selectedPresentation
                    .selected_price ||
                this.selectedPresentation
                    .price_default
            )

            return selectedPrice ===
                Number(priceIndex)
        },

        buildPayload(row, requestedIndex = null) {
            let priceIndex =
                requestedIndex !== null
                    ? Number(requestedIndex)
                    : this.getDefaultPriceIndex(row)

            if (!this.allowSelection) {
                priceIndex =
                    this.getDefaultPriceIndex(row)
            }

            if (!this.showMultiplePrices) {
                priceIndex = 2
            }

            const price = this.getPrice(
                row,
                priceIndex
            )

            return {
                row,

                price,

                priceIndex,

                presentation: {
                    ...row,

                    original_price_default:
                        Number(row.price_default),

                    selected_price:
                        priceIndex,

                    price_default:
                        priceIndex,

                    applied_price:
                        price,
                },
            }
        },

        selectPrice(row, priceIndex) {
            const payload = this.buildPayload(
                row,
                priceIndex
            )

            if (payload.price <= 0) {
                this.$message.warning(
                    'El precio seleccionado no está configurado'
                )

                return
            }

            this.$emit(
                'select-price',
                payload
            )
        },

        togglePresentation(row) {
            if (this.isSelectedPresentation(row)) {
                this.$emit('clear')

                return
            }

            const payload = this.buildPayload(row)

            this.$emit(
                'select-price',
                payload
            )
        },
    },
}
</script>

<style>
.presentation-price-selector {
    margin: 3px;
}

.presentation-price-selector .presentation-price-option {
    min-width: 76px;
    padding: 5px 9px;
    color: #606266;
    background: #ffffff;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s ease;
}

.presentation-price-selector .presentation-price-option:hover {
    color: #00a991;
    border-color: #00bfa5;
    background: #f0fffc;
}

.presentation-price-selector .presentation-price-option.selected {
    color: #ffffff;
    font-weight: 600;
    background: #00b19d;
    border-color: #00b19d;
}

.presentation-price-selector .presentation-price-option:disabled {
    color: #c0c4cc;
    background: #f5f7fa;
    border-color: #ebeef5;
    cursor: not-allowed;
}
</style>


// WEBPACK FOOTER //
// resources/js/components/PresentationPriceSelector.vue